import { InferGetServerSidePropsType } from 'next';
import { useIntl } from 'react-intl';

import { useTrackPageViewAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import {
    Breadcrumb,
    usePointOfInterestListBreadcrumbItems,
} from '../../../domains/Layout/Breadcrumb/Breadcrumb';
import { Footer } from '../../../domains/Layout/Footer/Footer';
import { Head } from '../../../domains/Layout/Head/Head';
import { useJsonLd } from '../../../domains/Layout/Head/JsonLd';
import { imageLoaderForId } from '../../../domains/Layout/Image/URL/ImageURL';
import { PrimaryNav } from '../../../domains/Layout/PrimaryNav/PrimaryNav';
import { getServerSideProps } from '../../../partials/point-of-interest-list/getServerSideProps';
import { PointOfInterestListHeader } from '../../../partials/point-of-interest-list/PointOfInterestListHeader/PointOfInterestListHeader';
import { PointOfInterestList } from '../../../partials/point-of-interest-list/shared/PointOfInterestList';
import { ItineraryListCarouselSection } from '../../../shared/components/ItineraryListCarouselSection/ItineraryListCarouselSection';
import { useLocale } from '../../../shared/hooks/useLocale';
import { useUrlGenerator } from '../../../shared/hooks/useUrlGenerator';
import { useCurrentSite } from '../../../shared/providers/currentSite/useCurrentSite';

export { getServerSideProps };

type PointOfInterestListPageProps = InferGetServerSidePropsType<typeof getServerSideProps>;

export default function PointOfInterestListPage(props: PointOfInterestListPageProps) {
    const { currentSite } = useCurrentSite();
    const { locale } = useLocale();
    const { formatMessage } = useIntl();
    const { generatePath, generateUrl } = useUrlGenerator();

    const {
        destination,
        pointsOfInterest,
        itineraries,
        destinationItinerariesCount,
        pointOfInterestListIntroduction,
        primaryNav,
        hrefLangs,
    } = props.data;

    const { currentPage, firstPage, lastPage } = pointsOfInterest.pagination;

    const canonicalUrl = generateUrl('point_of_interest_list', {
        destinationSlug: destination.slug,
        p: currentPage === 1 ? undefined : currentPage,
    });

    const { breadcrumbItems } = usePointOfInterestListBreadcrumbItems({ destination });

    const metaTitle = formatMessage(
        { id: 'page.poiList.meta.title' },
        {
            destinationPreposition: destination.preposition,
            destinationName: destination.name,
            page: currentPage,
        },
    );

    const metaDescription = formatMessage(
        { id: 'page.poiList.meta.description' },
        { destinationPreposition: destination.preposition, destinationName: destination.name },
    );

    const ogTitle = formatMessage(
        { id: 'page.poiList.ogp.title' },
        { destinationPreposition: destination.preposition, destinationName: destination.name },
    );

    const ogDescription = formatMessage(
        { id: 'page.poiList.ogp.description' },
        { destinationPreposition: destination.preposition, destinationName: destination.name },
    );

    const metaPrev =
        currentPage > firstPage
            ? generatePath('point_of_interest_list', {
                  destinationSlug: destination.slug,
                  p: currentPage > firstPage + 1 ? currentPage - 1 : undefined,
              })
            : undefined;

    const metaNext =
        currentPage < lastPage
            ? generatePath('point_of_interest_list', {
                  destinationSlug: destination.slug,
                  p: currentPage + 1,
              })
            : undefined;

    useTrackPageViewAmplitude((ampli, defaultProperties) => {
        ampli.viewedPagePoiList({
            ...defaultProperties,
            destination_id: destination.id,
            destination_slug: destination.slug,
            has_trip_form_available: destination.isAvailableForQuoteRequest,
        });
    });

    return (
        <>
            <Head
                title={metaTitle}
                description={metaDescription}
                canonical={canonicalUrl}
                hrefLangs={hrefLangs}
                prev={metaPrev}
                next={metaNext}
                og={{
                    title: ogTitle,
                    description: ogDescription,
                    image: imageLoaderForId({
                        aspectRatio: 1.91,
                    })({
                        src: destination.coverPicture.id.toString(),
                        width: 1200,
                    }),
                    locale,
                    url: canonicalUrl,
                    type: 'article',
                    site_name: currentSite.organisation.name,
                }}
                jsonLd={useJsonLd([])}
            />

            <PrimaryNav contextContinent={destination.continent} {...primaryNav} />

            <div className="layout-with-centered-content mb-48">
                <section className="main-container">
                    <Breadcrumb items={breadcrumbItems} />
                </section>
            </div>

            <div className="layout-with-centered-content mb-48">
                <section className="main-container">
                    <PointOfInterestListHeader
                        destination={destination}
                        introduction={pointOfInterestListIntroduction}
                    />
                </section>
            </div>

            {pointsOfInterest.items.length > 0 ? (
                <div className="layout-with-centered-content mb-40 last:mb-0 md:mb-96">
                    <section className="main-container">
                        <PointOfInterestList
                            destination={destination}
                            pointsOfInterest={pointsOfInterest.items}
                            pagination={pointsOfInterest.pagination}
                        />
                    </section>
                </div>
            ) : null}

            {itineraries.length > 0 ? (
                <div className="layout-with-centered-content mb-40 last:mb-0 md:mb-96">
                    <section className="main-container">
                        <ItineraryListCarouselSection
                            destination={destination}
                            itineraries={itineraries}
                            destinationItinerariesCount={destinationItinerariesCount}
                            title={{
                                as: 'h2',
                            }}
                        />
                    </section>
                </div>
            ) : null}

            <Footer
                destination={destination}
                topDestinations={primaryNav.destinationsMenu?.world.topDestinations ?? []}
            />
        </>
    );
}
